<script setup>
import {computed, nextTick, onMounted, ref} from 'vue'
import {getAppDomain, showSuccessMessage} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue'
import Select from '@/components/forms/Select.vue';
import {authData} from '@/services/AuthService';
import {ErrorMessage, Field, Form} from 'vee-validate';
import ContractTypeService from "@/services/ContractTypeService";
import {useI18n} from 'vue-i18n'
import ClientService from '@/services/ClientService';
import BSTooltip from '@/components/tooltips/BSTooltip.vue';

const {t} = useI18n();

/**
 * Text tooltipu u pole Poznámka (IMA-501).
 * Liší se podle tenantu (domény), ne podle jazyka – pobočka (SK/CZ) má český
 * text s B.I.G. Invest, CZ aplikace má IN Equity a SK aplikace slovenský text.
 */
const noteTooltip = computed(() => {
  const domain = getAppDomain();
  if (domain === 'SK') {
    return t('createUpdateClientModal.note_tooltip_sk');
  }
  if (domain === 'SK/CZ') {
    return t('createUpdateClientModal.note_tooltip_branch');
  }
  return t('createUpdateClientModal.note_tooltip_cz');
});

const props = defineProps({
  client: {type: Object, required: false},
  intermediariesPeople: {type: Array},
  accountManagers: {type: Array},
});

const config = ref(null);
const contractTypes = ref([]);

const client = ref({
  intermediary_person_id: '',
  account_manager_id: '',
  contract_type_id: null,
  client_type: 'FO',
  surname: '',
  firstname: '',
  title_before: '',
  title_after: '',
  email: '',
  phone: '',
  company_name: '',
  ico: '',
  ic_dph: '',
  registry_entry: '',
  personal_id: '',
  birth_date: '',
  birth_place: '',
  gender: '',
  marital_status: '',
  citizenship: '',
  nationality: '',
  tax_residence: '',
  tax_number: '',
  not_US_tax_residence: true,
  id_card_type: '',
  id_card_number: '',
  id_card_issued_by: '',
  id_card_issue_date: '',
  id_card_validity: '',
  employer_type: '',
  occupation: '',
  note: '',
  processing_fee_for_all_portfolios: false,
  processing_fee: '',
  active_access: true,
  mentor_name: '',
  mentor_in: '',
  mentor_number: '',
  mentor_address: '',
  legal_person_id: '',
  advisor_code: '',
  legal_guardian: '',
  referral_code: '',
  representative_title_before: '',
  representative_firstname: '',
  representative_surname: '',
  representative_title_after: '',
  representative_email: '',
  representative_phone: '',
  representative_personal_id: '',
  representative_birth_date: '',
  representative_birth_place: '',
  representative_position: '',
});

const visibleFields = computed(() =>
  config.value?.visible_fields?.[client.value.client_type] ?? []
);

// IMA-514: formulář PO má jiné uspořádání sekcí (bez jména klienta a osobních údajů)
const isPO = computed(() => client.value.client_type === 'PO');

// IMA-514: povinná pole PO platí jen při zakládání nové karty – existující PO
// (i z Bidli importu) musí jít uložit bez doplnění nově povinných údajů.
// Stejné pravidlo vynucuje BE v ClientSaveRequest.
const isNewRecord = computed(() => !client.value.id);

// IMA-516: doklad totožnosti je u PO povinný při založení I při úpravě karty (na rozdíl
// od IMA-514 polí výše). Starší PO záznamy se v DB nemění – doplnit doklad je nutné až
// ve chvíli, kdy někdo kartu otevře a ukládá. Stejné pravidlo vynucuje BE v ClientSaveRequest.
const idCardRequired = computed(() => isPO.value);

const availableClientTypes = ref([
  {value: 'FO', label: t('createUpdateClientModal.client_type_fo')},
  {value: 'PO', label: t('createUpdateClientModal.client_type_po')},
]);

const availableIdCardTypes = ref([
  {value: 'ID_CARD', label: t('createUpdateClientModal.id_card')},
  {value: 'PASSPORT', label: t('createUpdateClientModal.passport')},
  {value: 'DRIVING_LICENSE', label: t('createUpdateClientModal.driving_license')},
  {value: 'RESIDENCE_PERMIT', label: t('createUpdateClientModal.residence_permit')},
  {value: 'BIRTH_CERTIFICATE', label: t('createUpdateClientModal.birth_certificate')},
]);

const availableGenders = ref([
  {value: 'FEMALE', label: t('createUpdateClientModal.FEMALE')},
  {value: 'MALE', label: t('createUpdateClientModal.MALE')},
]);

const availableMaritalStatuses = ref([
  {value: 'SINGLE', label: t('createUpdateClientModal.SINGLE')},
  {value: 'MARRIED', label: t('createUpdateClientModal.MARRIED')},
  {value: 'REGISTERED_PARTNER', label: t('createUpdateClientModal.REGISTERED_PARTNER')},
  {value: 'DIVORCED', label: t('createUpdateClientModal.DIVORCED')},
  {value: 'WIDOWED', label: t('createUpdateClientModal.WIDOWED')},
  {value: 'OTHER', label: t('createUpdateClientModal.OTHER')},
]);

const availableNationalities = ref([
  {value: 'CZ', label: t('createUpdateClientModal.nationality_CZ')},
  {value: 'SK', label: t('createUpdateClientModal.nationality_SK')},
  {value: 'PL', label: t('createUpdateClientModal.nationality_PL')},
  {value: 'HU', label: t('createUpdateClientModal.nationality_HU')},
  {value: 'AT', label: t('createUpdateClientModal.nationality_AT')},
  {value: 'CH', label: t('createUpdateClientModal.nationality_CH')},
  {value: 'DE', label: t('createUpdateClientModal.nationality_DE')},
  {value: 'LI', label: t('createUpdateClientModal.nationality_LI')},
  {value: 'UA', label: t('createUpdateClientModal.nationality_UA')},
  {value: 'RS', label: t('createUpdateClientModal.nationality_RS')},
  // IMA-525: Kazachstán (seznam sdílí i daňová rezidence)
  {value: 'KZ', label: t('createUpdateClientModal.nationality_KZ')},
]);

const availableCitizenships = ref([
  {value: 'CZ', label: t('createUpdateClientModal.citizenship_CZ')},
  {value: 'SK', label: t('createUpdateClientModal.citizenship_SK')},
  {value: 'PL', label: t('createUpdateClientModal.citizenship_PL')},
  {value: 'HU', label: t('createUpdateClientModal.citizenship_HU')},
  {value: 'AT', label: t('createUpdateClientModal.citizenship_AT')},
  {value: 'CH', label: t('createUpdateClientModal.citizenship_CH')},
  {value: 'DE', label: t('createUpdateClientModal.citizenship_DE')},
  {value: 'LI', label: t('createUpdateClientModal.citizenship_LI')},
  {value: 'UA', label: t('createUpdateClientModal.citizenship_UA')},
  {value: 'RS', label: t('createUpdateClientModal.citizenship_RS')},
  // IMA-525: kazašské občanství
  {value: 'KZ', label: t('createUpdateClientModal.citizenship_KZ')},
]);

const availableEmployerTypes = ref([
  {value: 'EMPLOYEE', label: t('createUpdateClientModal.employer_type_EMPLOYEE')},
  {value: 'SZCO', label: t('createUpdateClientModal.employer_type_SZCO')},
  {value: 'MATERNITY', label: t('createUpdateClientModal.employer_type_MATERNITY')},
  {value: 'STUDENT', label: t('createUpdateClientModal.employer_type_STUDENT')},
  {value: 'PENSIONER', label: t('createUpdateClientModal.employer_type_PENSIONER')},
  {value: 'UNEMPLOYED', label: t('createUpdateClientModal.employer_type_UNEMPLOYED')},
  {value: 'STATUTORY', label: t('createUpdateClientModal.employer_type_STATUTORY')},
  {value: 'MINOR', label: t('createUpdateClientModal.employer_type_MINOR')},
]);

/**
 * Adds a fallback option for values that exist in the DB but are not in the
 * current options list (e.g. legacy free-text values or removed enum cases).
 * This prevents the select from silently showing blank on existing records.
 */
function optionsWithFallback(options, currentValue) {
  if (!currentValue || options.some(o => o.value === currentValue)) return options;
  return [...options, {value: currentValue, label: `— ${currentValue} —`}];
}

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);

/**
 * Scrolls the modal to the first invalid field after Vee-Validate rejects the form.
 * Field names match their id attributes, so getElementById works directly.
 */
function scrollToFirstError({ errors }) {
  const firstField = Object.keys(errors)[0];
  if (!firstField) return;
  nextTick(() => {
    const el = document.getElementById(firstField);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      // IMA-511: stav účtu se mění výhradně přes UpdateClientAccountStatusModal. Formulář by jinak
      // poslal hodnoty načtené při otevření detailu - pokud stav mezitím změnil někdo jiný, BO by ho
      // tiše přepsal zpět (a zapsal falešnou změnu do historie) a poradci by uložení selhalo na
      // oprávnění. BE má tato pole jako "sometimes", takže při vynechání zůstane stav beze změny.
      // eslint-disable-next-line no-unused-vars
      const {account_status, account_status_reason, access_deactivation_date, ...payload} = client.value;
      ClientService.saveClient(payload, client.value?.id).then(response => {
        emit('confirm', response);
        showSuccessMessage(response.message);
      }, () => {
      });
    } else {
      emit('cancel');
    }
  }, 300);
}

if (props.client) {
  client.value = {...client.value, ...props.client};
}

// IMA-514: Bidli import ukládá IČO do legal_person_id, formulář pracuje s polem ico.
// Předvyplníme ho, aby se u importovaných PO IČO zobrazilo a uložilo i do ico.
if (!client.value.ico && client.value.legal_person_id) {
  client.value.ico = client.value.legal_person_id;
}

onMounted(async () => {
  ContractTypeService.getAllActive().then((response) => {
    contractTypes.value = response;
  });
  ClientService.getConfig().then((response) => {
    config.value = response;
  }).catch(() => {
    config.value = {visible_fields: {FO: [], PO: []}};
  });
});
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" :static="true" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="client.id" as="h3" class="modal-title">
                {{ t('createUpdateClientModal.client_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateClientModal.client_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <Form v-on:submit="closeModalWith('confirm')" @invalid-submit="scrollToFirstError">
              <div class="modal-body">
                <template v-if="config !== null">
                  <fieldset class="mb-3">
                    <legend>{{ t('createUpdateClientModal.section_basic') }}</legend>
                    <div class="row mb-3">
                      <div class="col-12 col-md-6 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.client_type') }}</label>
                        <Select v-model="client.client_type" :options="availableClientTypes" class="w-100"/>
                      </div>
                    </div>
                    <div v-if="false" class="row mb-3">
                      <div class="col-12 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.administrator') }}</label>
                        <select v-model="client.account_manager_id" class="w-100 form-select form-control">
                          <option v-for="accountManager in accountManagers" :key="accountManager.id" :value="accountManager.id">
                            {{ accountManager.label }}
                          </option>
                        </select>
                      </div>
                    </div>
                    <div v-if="(authData?.loggedUser?.role === 'ADMIN' || authData?.loggedUser?.role === 'SUPERADMIN') && !client.id" class="row mb-3">
                      <div class="col-12 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.bound_agent') }}</label>
                        <select v-model="client.intermediary_person_id" class="w-100 form-select form-control">
                          <option v-for="person in intermediariesPeople" :key="person.id" :value="person.id">
                            {{ person.label }}
                          </option>
                        </select>
                      </div>
                    </div>
                    <div v-if="!client.id" class="row mb-3">
                      <div class="col-12 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.contract_type') }}</label>
                        <select v-model="client.contract_type_id" class="w-100 form-select form-control">
                          <option v-for="contractType in contractTypes" :key="contractType.id" :value="contractType.id">
                            {{ contractType.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                    <!-- IMA-514: tituly a jméno klienta jen u FO – u PO se jméno vyplňuje
                         v sekci Osoba jednající ve jménu PO (BE ho zrcadlí do firstname/surname) -->
                    <template v-if="!isPO">
                      <div class="row">
                        <div class="col-12 col-md-6 form-group">
                          <label for="titleBeforeInput" class="form-label">{{ t('createUpdateClientModal.title_before_name') }}</label>
                          <input v-model="client.title_before" id="titleBeforeInput" class="form-control" type="text"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="titleAfterInput" class="form-label">{{ t('createUpdateClientModal.title_behind_name') }}</label>
                          <input v-model="client.title_after" id="titleAfterInput" class="form-control" type="text"/>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12 col-md-6 form-group">
                          <label for="firstnameInput" class="form-label">{{ t('createUpdateClientModal.name') }}<span class="text-danger"> *</span></label>
                          <Field v-model="client.firstname" name="firstnameInput" id="firstnameInput" class="form-control" rules="required" required/>
                          <ErrorMessage name="firstnameInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="surnameInput" class="form-label">{{ t('createUpdateClientModal.surname') }}<span class="text-danger"> *</span></label>
                          <Field v-model="client.surname" name="surnameInput" id="surnameInput" class="form-control" rules="required" required/>
                          <ErrorMessage name="surnameInput"/>
                        </div>
                      </div>
                    </template>
                    <!-- IMA-514: u PO jsou firemní údaje nad e-mailem a telefonem. Název společnosti,
                         IČO a DIČ jsou povinné jen při zakládání nové karty (isNewRecord). -->
                    <div v-if="isPO" class="row mb-3">
                      <div class="col-12 form-group">
                        <label for="companyInput" class="form-label">{{ t('createUpdateClientModal.company_name') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                        <Field v-model="client.company_name" name="companyInput" id="companyInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                        <ErrorMessage name="companyInput"/>
                      </div>
                    </div>
                    <template v-if="visibleFields.includes('company_fields')">
                      <div class="row">
                        <div class="col-12 col-md-6 form-group">
                          <label for="icoInput" class="form-label">{{ t('createUpdateClientModal.ico') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.ico" name="icoInput" id="icoInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="icoInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="taxNumberInput" class="form-label">DIČ<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.tax_number" name="taxNumberInput" id="taxNumberInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="taxNumberInput"/>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12 col-md-6 form-group">
                          <label for="icDphInput" class="form-label">{{ t('createUpdateClientModal.ic_dph') }}</label>
                          <input v-model="client.ic_dph" id="icDphInput" class="form-control" type="text"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="registryEntryInput" class="form-label">{{ t('createUpdateClientModal.registry_entry') }}</label>
                          <input v-model="client.registry_entry" id="registryEntryInput" class="form-control" type="text"/>
                        </div>
                      </div>
                    </template>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label for="emailInput" class="form-label">{{ t('createUpdateClientModal.email') }}<span class="text-danger"> *</span></label>
                        <Field v-model="client.email" name="emailInput" id="emailInput" class="form-control" type="email" rules="required|email" required/>
                        <ErrorMessage name="emailInput"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="phoneNumber" class="form-label">{{ t('createUpdateClientModal.phone_number') }}<span class="text-danger"> *</span></label>
                        <Field v-model="client.phone" name="phoneNumber" id="phoneNumber" rules="required|phoneNumber" class="form-control" type="text" required/>
                        <ErrorMessage name="phoneNumber"/>
                      </div>
                    </div>
                  </fieldset>

                  <!-- IMA-514: u PO hned pod Základními údaji. Jméno, příjmení, e-mail a telefon
                       jsou povinné jen u nové karty. Pohlaví, rodinný stav, občanství a státní
                       příslušnost jsou vázané na pole klienta – u PO patří jednající osobě a
                       plní je i Bidli import, takže není potřeba nové sloupce. -->
                  <template v-if="visibleFields.includes('representative')">
                    <fieldset class="mb-3">
                      <legend>{{ t('createUpdateClientModal.section_representative') }}</legend>
                      <div class="row mb-3">
                        <div class="col-12 col-md-6 form-group">
                          <label for="repTitleBeforeInput" class="form-label">{{ t('createUpdateClientModal.representative_title_before') }}</label>
                          <input v-model="client.representative_title_before" id="repTitleBeforeInput" class="form-control" type="text"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="repTitleAfterInput" class="form-label">{{ t('createUpdateClientModal.representative_title_after') }}</label>
                          <input v-model="client.representative_title_after" id="repTitleAfterInput" class="form-control" type="text"/>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-12 col-md-6 form-group">
                          <label for="repFirstnameInput" class="form-label">{{ t('createUpdateClientModal.representative_firstname') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_firstname" name="repFirstnameInput" id="repFirstnameInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="repFirstnameInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="repSurnameInput" class="form-label">{{ t('createUpdateClientModal.representative_surname') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_surname" name="repSurnameInput" id="repSurnameInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="repSurnameInput"/>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-12 col-md-6 form-group">
                          <label for="repEmailInput" class="form-label">{{ t('createUpdateClientModal.representative_email') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_email" name="repEmailInput" id="repEmailInput" class="form-control" type="email" :rules="isNewRecord ? 'required|email' : 'email'"/>
                          <ErrorMessage name="repEmailInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="repPhoneInput" class="form-label">{{ t('createUpdateClientModal.representative_phone') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_phone" name="repPhoneInput" id="repPhoneInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="repPhoneInput"/>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-12 col-md-6 form-group">
                          <label for="repPersonalIdInput" class="form-label">{{ t('createUpdateClientModal.representative_personal_id') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_personal_id" name="repPersonalIdInput" id="repPersonalIdInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="repPersonalIdInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="repBirthDateInput" class="form-label">{{ t('createUpdateClientModal.representative_birth_date') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_birth_date" name="repBirthDateInput" id="repBirthDateInput" class="form-control" type="date" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="repBirthDateInput"/>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-12 col-md-6 form-group">
                          <label for="repBirthPlaceInput" class="form-label">{{ t('createUpdateClientModal.representative_birth_place') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_birth_place" name="repBirthPlaceInput" id="repBirthPlaceInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="repBirthPlaceInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="repPositionInput" class="form-label">{{ t('createUpdateClientModal.representative_position') }}<span v-if="isNewRecord" class="text-danger"> *</span></label>
                          <Field v-model="client.representative_position" name="repPositionInput" id="repPositionInput" class="form-control" type="text" :rules="isNewRecord ? 'required' : ''"/>
                          <ErrorMessage name="repPositionInput"/>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-12 col-md-6 form-group">
                          <label class="form-label">{{ t('createUpdateClientModal.gender') }}</label>
                          <Select v-model="client.gender" :options="optionsWithFallback(availableGenders, client.gender)" class="w-100"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label class="form-label">{{ t('createUpdateClientModal.marital_status') }}</label>
                          <Select v-model="client.marital_status" :options="optionsWithFallback(availableMaritalStatuses, client.marital_status)" class="w-100"/>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-12 col-md-6 form-group">
                          <label class="form-label">{{ t('createUpdateClientModal.citizenship') }}</label>
                          <Select v-model="client.citizenship" :options="optionsWithFallback(availableCitizenships, client.citizenship)" class="w-100"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label class="form-label">{{ t('createUpdateClientModal.nationality') }}</label>
                          <Select v-model="client.nationality" :options="optionsWithFallback(availableNationalities, client.nationality)" class="w-100"/>
                        </div>
                      </div>
                    </fieldset>
                  </template>


                  <!-- IMA-514: u PO se osobní údaje nezobrazují – pohlaví, rodinný stav, občanství
                       a státní příslušnost jsou v sekci Osoba jednající ve jménu PO -->
                  <fieldset v-if="!isPO" class="mb-3">
                    <legend>{{ t('createUpdateClientModal.section_personal') }}</legend>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label for="personalIdInput" class="form-label">{{ t('createUpdateClientModal.birth_number') }}</label>
                        <input v-model="client.personal_id" id="personalIdInput" class="form-control" type="text"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="birthDateInput" class="form-label">{{ t('createUpdateClientModal.birth_date') }}</label>
                        <input v-model="client.birth_date" id="birthDateInput" class="form-control" type="date"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label for="birthPlaceInput" class="form-label">{{ t('createUpdateClientModal.place_birth') }}</label>
                        <input v-model="client.birth_place" id="birthPlaceInput" class="form-control" type="text"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.gender') }}</label>
                        <Select v-model="client.gender" :options="optionsWithFallback(availableGenders, client.gender)" class="w-100"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.marital_status') }}</label>
                        <Select v-model="client.marital_status" :options="optionsWithFallback(availableMaritalStatuses, client.marital_status)" class="w-100"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.citizenship') }}</label>
                        <Select v-model="client.citizenship" :options="optionsWithFallback(availableCitizenships, client.citizenship)" class="w-100"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.nationality') }}</label>
                        <Select v-model="client.nationality" :options="optionsWithFallback(availableNationalities, client.nationality)" class="w-100"/>
                      </div>
                    </div>
                  </fieldset>

                  <fieldset class="mb-3">
                    <legend>{{ t('createUpdateClientModal.section_id_card') }}</legend>
                    <!-- IMA-516: u PO je všech 5 polí dokladu povinných (nová i upravovaná karta) -->
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label for="idCardTypeInput" class="form-label">{{ t('createUpdateClientModal.idcard_type') }}<span v-if="idCardRequired" class="text-danger"> *</span></label>
                        <!-- Select není nativní vee-validate input, proto ho Field obaluje přes v-slot;
                             id = name, aby scrollToFirstError našel prvek při nevalidním odeslání -->
                        <Field v-model="client.id_card_type" name="idCardTypeInput" :rules="idCardRequired ? 'required' : ''" v-slot="{ value, handleChange }">
                          <Select :model-value="value" @update:model-value="handleChange" id="idCardTypeInput" :options="optionsWithFallback(availableIdCardTypes, client.id_card_type)" class="w-100"/>
                        </Field>
                        <ErrorMessage name="idCardTypeInput"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="idCardNumberInput" class="form-label">{{ t('createUpdateClientModal.idcard_number') }}<span v-if="idCardRequired" class="text-danger"> *</span></label>
                        <Field v-model="client.id_card_number" name="idCardNumberInput" id="idCardNumberInput" class="form-control" type="text" :rules="idCardRequired ? 'required' : ''"/>
                        <ErrorMessage name="idCardNumberInput"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label for="idCardIssuedByInput" class="form-label">{{ t('createUpdateClientModal.idcard_issued_by') }}<span v-if="idCardRequired" class="text-danger"> *</span></label>
                        <Field v-model="client.id_card_issued_by" name="idCardIssuedByInput" id="idCardIssuedByInput" class="form-control" type="text" :rules="idCardRequired ? 'required' : ''"/>
                        <ErrorMessage name="idCardIssuedByInput"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="idCardIssueDateInput" class="form-label">{{ t('createUpdateClientModal.id_card_issue_date') }}<span v-if="idCardRequired" class="text-danger"> *</span></label>
                        <Field v-model="client.id_card_issue_date" name="idCardIssueDateInput" id="idCardIssueDateInput" class="form-control" type="date" :rules="idCardRequired ? 'required' : ''"/>
                        <ErrorMessage name="idCardIssueDateInput"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label for="idCardValidityInput" class="form-label">{{ t('createUpdateClientModal.idcard_validity') }}<span v-if="idCardRequired" class="text-danger"> *</span></label>
                        <Field v-model="client.id_card_validity" name="idCardValidityInput" id="idCardValidityInput" class="form-control" type="date" :rules="idCardRequired ? 'required' : ''"/>
                        <ErrorMessage name="idCardValidityInput"/>
                      </div>
                    </div>
                  </fieldset>

                  <fieldset class="mb-3">
                    <legend>{{ t('createUpdateClientModal.section_tax_residence') }}</legend>
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.tax_residence') }}</label>
                        <!-- optionsWithFallback handles legacy free-text values stored in DB (e.g. "Česká republika") -->
                        <Select v-model="client.tax_residence" :options="optionsWithFallback(availableNationalities, client.tax_residence)" class="w-100"/>
                      </div>
                      <div v-if="!visibleFields.includes('company_fields')" class="col-12 col-md-6 form-group">
                        <label for="tinInput" class="form-label">TIN</label>
                        <input v-model="client.tax_number" id="tinInput" class="form-control" type="text"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 form-group">
                        <div class="form-check">
                          <input v-model="client.not_US_tax_residence" id="notUSTaxResidenceCheckbox" type="checkbox" class="form-check-input"/>
                          <label for="notUSTaxResidenceCheckbox" class="form-check-label">{{ t('createUpdateClientModal.not_US_tax_residence') }}</label>
                        </div>
                      </div>
                    </div>
                  </fieldset>

                  <fieldset class="mb-3">
                    <legend>{{ t('createUpdateClientModal.section_additional') }}</legend>
                    <!-- Typ zaměstnance / Povolání dává smysl jen u fyzické osoby, ne u PO (firmy) -->
                    <div v-if="!isPO" class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label class="form-label">{{ t('createUpdateClientModal.employer_type') }}</label>
                        <Select v-model="client.employer_type" :options="optionsWithFallback(availableEmployerTypes, client.employer_type)" class="w-100"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="occupationInput" class="form-label">{{ t('createUpdateClientModal.occupation') }}</label>
                        <input v-model="client.occupation" id="occupationInput" class="form-control" type="text"/>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-12 form-group">
                        <label for="noteInput" class="form-label">{{ t('createUpdateClientModal.note') }}
                          <BSTooltip :title="noteTooltip">
                            <i class="mdi mdi-information-outline ms-1"/>
                          </BSTooltip>
                        </label>
                        <textarea v-model="client.note" id="noteInput" class="form-control"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 form-group">
                        <div class="form-check">
                          <input v-model="client.processing_fee_for_all_portfolios" id="processingFeeForAllPortfolios" type="checkbox" class="form-check-input"/>
                          <label for="processingFeeForAllPortfolios" class="form-check-label">{{ t('createUpdateClientModal.processing_fee_for_all_portfolios') }}</label>
                        </div>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-12 form-group">
                        <label for="processingFee" class="form-label">{{ t('createUpdateClientModal.processing_fee') }}</label>
                        <textarea v-model="client.processing_fee" id="processingFee" class="form-control"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 form-group">
                        <div class="form-check">
                          <input v-model="client.active_access" id="hasActiveAccess" type="checkbox" class="form-check-input"/>
                          <label for="hasActiveAccess" class="form-check-label">{{ t('createUpdateClientModal.active_access') }}</label>
                        </div>
                      </div>
                    </div>
                  </fieldset>

                </template>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateClientModal.cancel') }}</button>
                <button class="btn btn-primary btn-md">{{ t('createUpdateClientModal.save') }}</button>
              </div>
            </Form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>